import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { saveDraft, loadDraft, clearDraft } from '../../lib/session';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Car, CheckCircle, XCircle, Plus, Eye, Edit, Trash2, Star, Printer, Upload, Download, Search, ArrowUpDown, ChevronUp, ChevronDown, Trophy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function CarsTab({ 
  currentEvent, 
  cars, 
  setCars, 
  classes, 
  fetchCars,
  carsLastUpdated
}) {
  const navigate = useNavigate();
  
  // Local state
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const SORT_PREFS_KEY = currentEvent?.id ? `cars-sort:${currentEvent.id}` : 'cars-sort:default';
  const [sortField, setSortField] = useState(() => {
    try { return localStorage.getItem(`${SORT_PREFS_KEY}:field`) || 'registration_number'; }
    catch (_e) { return 'registration_number'; }
  });
  const [sortDirection, setSortDirection] = useState(() => {
    try { return localStorage.getItem(`${SORT_PREFS_KEY}:dir`) || 'asc'; }
    catch (_e) { return 'asc'; }
  });
  // Persist sort preferences so the Car List remembers them across page
  // refreshes / token expiry / device reloads during a multi-hour show.
  useEffect(() => {
    try {
      localStorage.setItem(`${SORT_PREFS_KEY}:field`, sortField);
      localStorage.setItem(`${SORT_PREFS_KEY}:dir`, sortDirection);
    } catch (_e) {}
  }, [SORT_PREFS_KEY, sortField, sortDirection]);
  // When switching to a different event, re-hydrate sort prefs for THAT event.
  useEffect(() => {
    try {
      const f = localStorage.getItem(`${SORT_PREFS_KEY}:field`);
      const d = localStorage.getItem(`${SORT_PREFS_KEY}:dir`);
      if (f) setSortField(f);
      if (d) setSortDirection(d);
    } catch (_e) {}
  }, [SORT_PREFS_KEY]);

  const [showManualForm, setShowManualForm] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // CSV Upload state
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [csvUploadResult, setCsvUploadResult] = useState(null);
  
  // Manual registration form
  const DRAFT_KEY = currentEvent?.id ? `manual-reg:${currentEvent.id}` : null;
  const emptyManualForm = {
    owner_name: '',
    owner_email: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    class_name: '',
    photo_data: ''
  };
  const [manualForm, setManualForm] = useState(emptyManualForm);
  const [draftRestoredNotice, setDraftRestoredNotice] = useState(false);

  // Restore any saved draft for THIS event when the tab/component mounts,
  // so a token expiry redirect or accidental refresh doesn't wipe what the
  // admin just typed. Drafts are scoped per event so switching events
  // doesn't accidentally cross-pollute.
  const restoredKeyRef = useRef(null);
  useEffect(() => {
    if (!DRAFT_KEY) return;
    if (restoredKeyRef.current === DRAFT_KEY) return;
    const saved = loadDraft(DRAFT_KEY);
    if (saved && (saved.owner_name || saved.make || saved.model || saved.class_name)) {
      setManualForm({ ...emptyManualForm, ...saved });
      setDraftRestoredNotice(true);
      setShowManualForm(true);
    }
    restoredKeyRef.current = DRAFT_KEY;
  }, [DRAFT_KEY]);

  // Persist every keystroke (cheap — localStorage write is sync but tiny).
  useEffect(() => {
    if (!DRAFT_KEY) return;
    const hasContent =
      manualForm.owner_name || manualForm.make || manualForm.model || manualForm.class_name;
    if (hasContent) {
      saveDraft(DRAFT_KEY, manualForm);
    }
  }, [DRAFT_KEY, manualForm]);

  // CSV Upload Functions
  const handleDownloadCSVTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cars/csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'car_registration_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploadingCSV(true);
    setCsvUploadResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/cars/bulk-upload?event_id=${currentEvent.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setCsvUploadResult(response.data);
      
      if (response.data.cars_created > 0) {
        toast.success(`Successfully uploaded ${response.data.cars_created} car(s)`);
        fetchCars();
      }
      
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} row(s) had errors`);
      }
      
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploadingCSV(false);
      event.target.value = '';
    }
  };

  const handleToggleCheckIn = async (carId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cars/${carId}/check-in`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCars(cars.map(car => 
        car.id === carId ? { ...car, checked_in: !currentStatus } : car
      ));
      
      toast.success(currentStatus ? 'Car checked out' : 'Car checked in');
    } catch (error) {
      console.error('Error toggling check-in:', error);
      toast.error('Failed to update check-in status');
    }
  };

  const handleApproveCar = async (carId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cars/${carId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Car approved successfully');
      fetchCars();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error approving car:', error);
      toast.error('Failed to approve car');
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Are you sure you want to delete this car registration?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cars/${carId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Car deleted successfully');
      fetchCars();
      setShowDetailModal(false);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error deleting car:', error);
      toast.error('Failed to delete car');
    }
  };

  const handleViewCar = (car) => {
    setSelectedCar(car);
    setShowDetailModal(true);
  };

  const handleEditCar = (car) => {
    setSelectedCar(car);
    setEditForm({
      owner_name: car.owner_name,
      owner_email: car.owner_email,
      make: car.make,
      model: car.model,
      year: car.year,
      class_name: car.class_name,
      photo_data: car.photo_data || ''
    });
    setShowEditModal(true);
    setShowDetailModal(false);
  };

  const handleUpdateCar = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/cars/${selectedCar.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Car updated successfully');
      setShowEditModal(false);
      fetchCars();
    } catch (error) {
      console.error('Error updating car:', error);
      toast.error(error.response?.data?.detail || 'Failed to update car');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/cars/manual?event_id=${currentEvent.id}`,
        manualForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newCar = response.data;
      
      toast.success('Car registered successfully');
      setShowManualForm(false);
      setManualForm(emptyManualForm);
      if (DRAFT_KEY) clearDraft(DRAFT_KEY);
      setDraftRestoredNotice(false);
      fetchCars();
      
      if (window.confirm(`Registration #${newCar.registration_number} created!\n\nWould you like to print the dash card now?`)) {
        navigate(`/admin/print-dash-cards?ids=${newCar.id}`);
      }
    } catch (error) {
      console.error('Error registering car:', error);
      toast.error(error.response?.data?.detail || 'Failed to register car');
    } finally {
      setSubmitting(false);
    }
  };

  // Tick once a second so the "X seconds ago" indicator stays accurate
  // without re-fetching anything. Cheap — just a state bump.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const formatRelative = (date) => {
    if (!date) return 'never';
    const secs = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 1000));
    if (secs < 5) return 'just now';
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ago`;
  };

  // Filter, search, and sort cars
  const filteredCars = (cars || [])
    .filter(car => {
      if (filterStatus === 'pending' && car.approved) return false;
      if (filterStatus === 'approved' && !car.approved) return false;
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchFields = [
          car.registration_number,
          car.owner_name,
          car.owner_email,
          car.make,
          car.model,
          String(car.year),
          car.class_name
        ].map(f => (f || '').toLowerCase());
        
        if (!searchFields.some(field => field.includes(query))) {
          return false;
        }
      }
      
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'registration_number':
          aVal = parseInt(a.registration_number) || 0;
          bVal = parseInt(b.registration_number) || 0;
          break;
        case 'owner_name':
          aVal = (a.owner_name || '').toLowerCase();
          bVal = (b.owner_name || '').toLowerCase();
          break;
        case 'vehicle':
          aVal = `${a.year} ${a.make} ${a.model}`.toLowerCase();
          bVal = `${b.year} ${b.make} ${b.model}`.toLowerCase();
          break;
        case 'class_name':
          aVal = (a.class_name || '').toLowerCase();
          bVal = (b.class_name || '').toLowerCase();
          break;
        case 'created_at':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        default:
          aVal = a[sortField];
          bVal = b[sortField];
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const pendingCount = (cars || []).filter(c => !c.approved).length;
  const approvedCount = (cars || []).filter(c => c.approved).length;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-700 text-sm">Total Cars</p>
              <p className="text-3xl font-bold text-gray-900">{(cars || []).length}</p>
            </div>
            <Car className="w-12 h-12 text-blue-500" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-700 text-sm">Pending Approval</p>
              <p className="text-3xl font-bold text-yellow-500">{pendingCount}</p>
            </div>
            <XCircle className="w-12 h-12 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-700 text-sm">Approved</p>
              <p className="text-3xl font-bold text-green-500">{approvedCount}</p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
        </div>
      </div>

      {/* Live update indicator (auto-refresh every 10s while on this tab) */}
      <div className="flex items-center justify-end gap-3 text-xs text-gray-600 mb-2" data-testid="cars-live-status">
        <span className="inline-flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          Live — auto-refreshes every 10s
        </span>
        <span aria-hidden="true">•</span>
        <span data-testid="cars-last-updated">Updated {formatRelative(carsLastUpdated)}</span>
        <button
          type="button"
          onClick={() => fetchCars()}
          className="inline-flex items-center gap-1 text-gray-700 hover:text-pink-600 underline"
          data-testid="cars-refresh-now-btn"
          title="Refresh now"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh now
        </button>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Button 
          onClick={() => setShowManualForm(!showManualForm)}
          className="bg-pink-600 hover:bg-pink-700"
          data-testid="manual-registration-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Manual Registration
        </Button>
        
        <Button
          onClick={() => navigate('/admin/print-dash-cards?all=true')}
          variant="outline"
          className="border-gray-300 text-gray-700"
          disabled={approvedCount === 0}
          data-testid="print-all-dash-cards-btn"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print All Dash Cards ({approvedCount})
        </Button>

        <Button
          onClick={() => navigate('/leaderboard')}
          variant="outline"
          className="border-gray-300 text-gray-700"
          data-testid="view-leaderboard-btn"
        >
          <Trophy className="w-4 h-4 mr-2" />
          View Leaderboard
        </Button>

        <Button
          onClick={handleDownloadCSVTemplate}
          variant="outline"
          className="border-gray-300 text-gray-700"
          data-testid="download-csv-template-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Download Template
        </Button>
        
        <label className="cursor-pointer">
          <Button
            variant="outline"
            className="border-gray-300 text-gray-700"
            disabled={uploadingCSV}
            asChild
          >
            <span>
              <Upload className="w-4 h-4 mr-2" />
              {uploadingCSV ? 'Uploading...' : 'Upload CSV'}
            </span>
          </Button>
          <input
            type="file"
            accept=".csv"
            onChange={handleCSVUpload}
            className="hidden"
            disabled={uploadingCSV}
            data-testid="csv-upload-input"
          />
        </label>

        <div className="flex gap-2 ml-auto">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('all')}
            data-testid="filter-all-btn"
          >
            All ({(cars || []).length})
          </Button>
          <Button
            variant={filterStatus === 'pending' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('pending')}
            className={filterStatus === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}
            data-testid="filter-pending-btn"
          >
            Pending ({pendingCount})
          </Button>
          <Button
            variant={filterStatus === 'approved' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('approved')}
            className={filterStatus === 'approved' ? 'bg-green-500 hover:bg-green-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}
            data-testid="filter-approved-btn"
          >
            Approved ({approvedCount})
          </Button>
        </div>
      </div>

      {/* Search and Sort Bar */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search by reg #, name, email, vehicle, class..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white border-gray-300 text-gray-900"
            data-testid="cars-search-input"
          />
        </div>
        
        <Select value={sortField} onValueChange={setSortField}>
          <SelectTrigger className="w-[180px] bg-white border-gray-300 text-gray-900" data-testid="sort-field-select">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="registration_number" className="text-gray-900">Reg #</SelectItem>
            <SelectItem value="owner_name" className="text-gray-900">Owner Name</SelectItem>
            <SelectItem value="vehicle" className="text-gray-900">Vehicle</SelectItem>
            <SelectItem value="class_name" className="text-gray-900">Class</SelectItem>
            <SelectItem value="created_at" className="text-gray-900">Date Added</SelectItem>
          </SelectContent>
        </Select>
        
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
          data-testid="sort-direction-btn"
        >
          {sortDirection === 'asc' ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
        
        {searchQuery && (
          <p className="text-gray-600 text-sm self-center">
            Found {filteredCars.length} result{filteredCars.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* CSV Upload Results */}
      {csvUploadResult && (
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6 mb-8">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-bold text-gray-900">CSV Upload Results</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCsvUploadResult(null)}
            >
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600 text-2xl font-bold">{csvUploadResult.cars_created}</p>
              <p className="text-green-600 text-sm">Cars Created (Auto-Approved)</p>
            </div>
            <div className="p-4 bg-red-500/10 border border-pink-500/20 rounded-lg">
              <p className="text-red-400 text-2xl font-bold">{csvUploadResult.errors}</p>
              <p className="text-red-300 text-sm">Errors</p>
            </div>
          </div>
          
          {csvUploadResult.details?.error_messages?.length > 0 && (
            <div className="mt-4">
              <p className="text-red-400 font-medium mb-2">Error Details:</p>
              <ul className="text-red-300 text-sm space-y-1 max-h-40 overflow-y-auto">
                {csvUploadResult.details.error_messages.map((err, i) => (
                  <li key={i}>• {err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Manual Registration Form */}
      {showManualForm && (
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-6 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Manual Car Registration</h3>
          {draftRestoredNotice && (
            <div className="flex items-start justify-between gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900" data-testid="draft-restored-banner">
              <div>
                <strong>Draft restored.</strong> The fields below were saved
                from a previous session so nothing you typed was lost. Submit
                to save the registration, or discard the draft to start over.
              </div>
              <button
                type="button"
                onClick={() => {
                  setManualForm(emptyManualForm);
                  if (DRAFT_KEY) clearDraft(DRAFT_KEY);
                  setDraftRestoredNotice(false);
                }}
                className="text-amber-900 underline whitespace-nowrap"
                data-testid="discard-draft-btn"
              >
                Discard draft
              </button>
            </div>
          )}
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Owner Name *</Label>
                <Input
                  value={manualForm.owner_name}
                  onChange={(e) => setManualForm({...manualForm, owner_name: e.target.value})}
                  required
                  placeholder="John Doe"
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="manual-owner-name"
                />
              </div>
              <div>
                <Label>Owner Email *</Label>
                <Input
                  type="email"
                  value={manualForm.owner_email}
                  onChange={(e) => setManualForm({...manualForm, owner_email: e.target.value})}
                  required
                  placeholder="john@example.com"
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="manual-owner-email"
                />
              </div>
              <div>
                <Label>Make *</Label>
                <Input
                  value={manualForm.make}
                  onChange={(e) => setManualForm({...manualForm, make: e.target.value})}
                  required
                  placeholder="Ford"
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="manual-make"
                />
              </div>
              <div>
                <Label>Model *</Label>
                <Input
                  value={manualForm.model}
                  onChange={(e) => setManualForm({...manualForm, model: e.target.value})}
                  required
                  placeholder="Mustang"
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="manual-model"
                />
              </div>
              <div>
                <Label>Year *</Label>
                <Input
                  type="number"
                  value={manualForm.year}
                  onChange={(e) => setManualForm({...manualForm, year: parseInt(e.target.value)})}
                  required
                  min="1900"
                  max="2030"
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="manual-year"
                />
              </div>
              <div>
                <Label>Class *</Label>
                <Select
                  value={manualForm.class_name}
                  onValueChange={(value) => setManualForm({...manualForm, class_name: value})}
                >
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900" data-testid="manual-class-select">
                    <SelectValue placeholder="Select a class" />
                  </SelectTrigger>
                  <SelectContent>
                    {(classes || []).length === 0 ? (
                      <SelectItem value="loading" disabled>Loading classes...</SelectItem>
                    ) : (
                      (classes || []).map(cls => (
                        <SelectItem key={cls.id} value={cls.name}>{cls.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Photo Upload */}
            <div className="space-y-2">
              <Label>Car Photo (Optional)</Label>
              {manualForm.photo_data ? (
                <div className="relative inline-block">
                  <img 
                    src={manualForm.photo_data} 
                    alt="Car" 
                    className="h-32 object-cover rounded-lg"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="absolute top-1 right-1"
                    onClick={() => setManualForm({...manualForm, photo_data: ''})}
                  >
                    ✕
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('Image must be less than 5MB');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setManualForm({...manualForm, photo_data: event.target.result});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                    id="manual-photo-upload"
                  />
                  <label htmlFor="manual-photo-upload" className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Upload Photo
                  </label>
                  <span className="text-xs text-gray-600">PNG, JPG up to 5MB</span>
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <Button type="submit" disabled={submitting} className="bg-pink-600 hover:bg-pink-700" data-testid="manual-submit-btn">
                {submitting ? 'Registering...' : 'Register Car'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowManualForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Cars Table */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] table-fixed" data-testid="cars-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th 
                  className="w-[60px] px-2 py-4 text-left text-sm font-semibold cursor-pointer hover:text-gray-900"
                  style={{ color: '#374151' }}
                  onClick={() => handleSort('registration_number')}
                >
                  <span className="flex items-center gap-1" style={{ color: '#374151' }}>
                    Reg #
                    {sortField === 'registration_number' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </span>
                </th>
                <th 
                  className="w-[160px] px-2 py-4 text-left text-sm font-semibold cursor-pointer hover:text-gray-900"
                  style={{ color: '#374151' }}
                  onClick={() => handleSort('owner_name')}
                >
                  <span className="flex items-center gap-1" style={{ color: '#374151' }}>
                    Owner
                    {sortField === 'owner_name' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </span>
                </th>
                <th 
                  className="w-[150px] px-2 py-4 text-left text-sm font-semibold cursor-pointer hover:text-gray-900"
                  style={{ color: '#374151' }}
                  onClick={() => handleSort('vehicle')}
                >
                  <span className="flex items-center gap-1" style={{ color: '#374151' }}>
                    Vehicle
                    {sortField === 'vehicle' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </span>
                </th>
                <th 
                  className="px-2 py-4 text-left text-sm font-semibold cursor-pointer hover:text-gray-900"
                  style={{ color: '#374151' }}
                  onClick={() => handleSort('class_name')}
                >
                  <span className="flex items-center gap-1" style={{ color: '#374151' }}>
                    Class
                    {sortField === 'class_name' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </span>
                </th>
                <th className="w-[60px] px-2 py-4 text-left text-sm font-semibold" style={{ color: '#374151' }}>Src</th>
                <th className="w-[75px] px-2 py-4 text-left text-sm font-semibold" style={{ color: '#374151' }}>Status</th>
                <th className="w-[70px] px-2 py-4 text-center text-sm font-semibold" style={{ color: '#374151' }}>Check-In</th>
                <th className="w-[55px] px-2 py-4 text-center text-sm font-semibold" style={{ color: '#374151' }}>Judged</th>
                <th className="w-[120px] px-2 py-4 text-right text-sm font-semibold" style={{ color: '#374151' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCars.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-600">
                    {searchQuery ? 'No cars match your search' : 'No cars found'}
                  </td>
                </tr>
              ) : (
                filteredCars.map(car => (
                  <tr key={car.id} className="hover:bg-gray-50 transition-colors" data-testid={`car-row-${car.registration_number}`}>
                    <td className="px-2 py-3 text-gray-900 font-mono text-sm">#{car.registration_number}</td>
                    <td className="px-2 py-3">
                      <div className="text-gray-900 text-sm truncate" title={car.owner_name}>{car.owner_name}</div>
                      <div className="text-xs text-gray-600 truncate" title={car.owner_email}>{car.owner_email}</div>
                    </td>
                    <td className="px-2 py-3">
                      <div className="text-gray-900 text-sm truncate" title={`${car.year} ${car.make} ${car.model}`}>
                        {car.year} {car.make} {car.model}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-gray-900 text-sm truncate" title={car.class_name}>{car.class_name}</td>
                    <td className="px-2 py-3">
                      <div className="text-xs">
                        {car.order_source === 'woocommerce' ? (
                          <span className="text-blue-600">WC</span>
                        ) : (
                          <span className="text-purple-600">Manual</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-3">
                      {car.approved ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-600 border border-green-500/30">
                          <CheckCircle className="w-3 h-3 mr-0.5" />
                          OK
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleApproveCar(car.id)}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-300 hover:bg-green-100 hover:text-green-700 hover:border-green-300 active:scale-95 transition-colors"
                          title="Tap to approve"
                          data-testid={`status-approve-btn-${car.registration_number}`}
                        >
                          <XCircle className="w-3 h-3 mr-0.5" />
                          Pend ✓
                        </button>
                      )}
                    </td>
                    <td className="px-2 py-3 text-center">
                      <button
                        onClick={() => handleToggleCheckIn(car.id, car.checked_in)}
                        className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                          car.checked_in
                            ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                        }`}
                        data-testid={`checkin-btn-${car.registration_number}`}
                      >
                        {car.checked_in ? '✓ In' : 'Out'}
                      </button>
                    </td>
                    <td className="px-2 py-3 text-center">
                      {car.has_been_judged ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-300">
                          <Star className="w-3 h-3" />
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-2 py-3 text-right">
                      <div className="flex justify-end gap-0.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewCar(car)}
                          className="h-7 w-7 p-0"
                          data-testid={`view-btn-${car.registration_number}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditCar(car)}
                          className="h-7 w-7 p-0"
                          data-testid={`edit-btn-${car.registration_number}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/print-dash-cards?ids=${car.id}`)}
                          title="Print Dash Card"
                          className="h-7 w-7 p-0"
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                        {!car.approved && (
                          <Button
                            size="sm"
                            onClick={() => handleApproveCar(car.id)}
                            className="bg-green-600 hover:bg-green-700 h-7 px-1.5 text-xs"
                            data-testid={`approve-btn-${car.registration_number}`}
                          >
                            ✓
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Car Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl bg-white text-gray-900 border-gray-300">
          {selectedCar && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl text-gray-900 font-bold">
                  Car Details - #{selectedCar.registration_number}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Full registration information
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {selectedCar.photo_data && (
                  <div>
                    <img 
                      src={selectedCar.photo_data} 
                      alt={`${selectedCar.year} ${selectedCar.make} ${selectedCar.model}`}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  </div>
                )}
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Owner Name</Label>
                    <p className="text-gray-900">{selectedCar.owner_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Owner Email</Label>
                    <p className="text-gray-900">{selectedCar.owner_email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Make</Label>
                    <p className="text-gray-900">{selectedCar.make}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Model</Label>
                    <p className="text-gray-900">{selectedCar.model}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Year</Label>
                    <p className="text-gray-900">{selectedCar.year}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Class</Label>
                    <p className="text-gray-900">{selectedCar.class_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Source</Label>
                    <p className="text-gray-900 capitalize">{selectedCar.order_source}</p>
                  </div>
                  {selectedCar.order_id && selectedCar.order_id !== 'MANUAL' && (
                    <div>
                      <Label className="text-gray-600">Order ID</Label>
                      <p className="text-gray-900">#{selectedCar.order_id}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-gray-600">Status</Label>
                    <p className="text-gray-900">{selectedCar.approved ? 'Approved' : 'Pending'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Checked In</Label>
                    <p className="text-gray-900">{selectedCar.checked_in ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => handleEditCar(selectedCar)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                {!selectedCar.approved && (
                  <Button onClick={() => handleApproveCar(selectedCar.id)} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                )}
                <Button variant="destructive" onClick={() => handleDeleteCar(selectedCar.id)}>
                  Delete
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Car Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl bg-white text-gray-900 border-gray-300">
          {selectedCar && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl text-gray-900 font-bold">
                  Edit Car - #{selectedCar.registration_number}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Update car registration details
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleUpdateCar} className="space-y-4">
                {/* Photo Upload Section */}
                <div className="space-y-2">
                  <Label>Car Photo</Label>
                  {editForm.photo_data ? (
                    <div className="relative">
                      <img 
                        src={editForm.photo_data} 
                        alt="Car" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute top-2 right-2"
                        onClick={() => setEditForm({...editForm, photo_data: ''})}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error('Image must be less than 5MB');
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              setEditForm({...editForm, photo_data: event.target.result});
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        <div className="text-gray-600">
                          <svg className="mx-auto h-12 w-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm">Click to upload car photo</p>
                          <p className="text-xs text-gray-600 mt-1">PNG, JPG up to 5MB</p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Owner Name *</Label>
                    <Input
                      value={editForm.owner_name}
                      onChange={(e) => setEditForm({...editForm, owner_name: e.target.value})}
                      required
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                  <div>
                    <Label>Owner Email *</Label>
                    <Input
                      type="email"
                      value={editForm.owner_email}
                      onChange={(e) => setEditForm({...editForm, owner_email: e.target.value})}
                      required
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                  <div>
                    <Label>Make *</Label>
                    <Input
                      value={editForm.make}
                      onChange={(e) => setEditForm({...editForm, make: e.target.value})}
                      required
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                  <div>
                    <Label>Model *</Label>
                    <Input
                      value={editForm.model}
                      onChange={(e) => setEditForm({...editForm, model: e.target.value})}
                      required
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                  <div>
                    <Label>Year *</Label>
                    <Input
                      type="number"
                      value={editForm.year}
                      onChange={(e) => setEditForm({...editForm, year: parseInt(e.target.value)})}
                      required
                      min="1900"
                      max="2030"
                      className="bg-white border-gray-300 text-gray-900"
                    />
                  </div>
                  <div>
                    <Label>Class *</Label>
                    <Select
                      value={editForm.class_name}
                      onValueChange={(value) => setEditForm({...editForm, class_name: value})}
                    >
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                        <SelectValue placeholder="Select a class" />
                      </SelectTrigger>
                      <SelectContent>
                        {(classes || []).length === 0 ? (
                          <SelectItem value="loading" disabled>Loading classes...</SelectItem>
                        ) : (
                          (classes || []).map(cls => (
                            <SelectItem key={cls.id} value={cls.name}>{cls.name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <DialogFooter className="flex justify-between">
                  <Button 
                    type="button" 
                    variant="destructive" 
                    onClick={() => handleDeleteCar(selectedCar.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Registration
                  </Button>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="bg-pink-600 hover:bg-pink-700">
                      {submitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
